// @ts-nocheck
import { handleError } from '@/utilities';

let auth;
let baseUrl;
let refreshSession;
let abortController;

function handleAbort() {
  if (abortController) {
    abortController.abort();
  }
}

function setupAbortController() {
  if (!abortController) {
    abortController = new AbortController();
    window.addEventListener('beforeunload', handleAbort);
  }
}

export async function setAuthentication(inAuth) {
  auth = inAuth;
}

export function initialize({
  auth: inAuth,
  refreshSession: inRefreshSession,
  siteApiUrl,
  projectId,
}) {
  refreshSession = inRefreshSession;
  setAuthentication(inAuth);
  baseUrl = `${siteApiUrl}/site-v1/${projectId}`;
  setupAbortController();
}

function filterErrors(error) {
  if (error.name === 'CanceledError') {
    // Ignore errors
    return;
  }
  // Handle or re-throw other types of errors.
  throw error;
}

function getDomain() {
  let domain;
  try {
    domain = window.location.hostname;
    if (!domain) {
      const url = document.documentURI;
      if (url) {
        domain = new URL(url).hostname;
      }
    }
  } catch (err) {
    handleError(err);
    // ignore errors
  }
  return domain;
}

async function fetchWithAuth(
  url,
  options = {},
  maxAttempts = 3,
  backoff = 300,
) {
  const attemptFetch = async (attempt) => {
    try {
      const headers = {
        ...options.headers,
        // Add x-session to headers if available
        ...(auth?.session && { 'x-session': auth.session }),
      };
      const response = await fetch(url, {
        ...options,
        headers,
        signal: abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP error encountered. Status: ${response.status}`);
      }

      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        const data = await response.json();
        return data;
      }
      const responseText = await response.text();
      return responseText;
    } catch (error) {
      if (attempt < maxAttempts) {
        await new Promise((resolve) => {
          setTimeout(resolve, backoff);
        });
        return attemptFetch(attempt + 1);
      }
      handleError(error);
      throw error;
    }
  };

  return attemptFetch(1);
}

export async function getSession(cordId) {
  const url = new URL(`${baseUrl}/session`);

  if (cordId) {
    url.searchParams.append('_cord_id', cordId);
  }
  url.searchParams.append('useragent', navigator.userAgent);
  url.searchParams.append('domain', getDomain());

  return fetchWithAuth(url.toString()).catch(filterErrors);
}

async function ensureAuth() {
  if (refreshSession && (!auth || !auth.session)) {
    await refreshSession();
    if (!auth) {
      throw new Error('Not authorized');
    }
  }
}

export async function getProfileId() {
  await ensureAuth();
  return fetchWithAuth(`${baseUrl}/profile/id`).catch(filterErrors);
}

export async function postProfile(payload) {
  await ensureAuth();
  return fetchWithAuth(`${baseUrl}/profile`, {
    method: 'POST',
    body: JSON.stringify(payload),
    headers: {
      'Content-Type': 'application/json',
    },
  }).catch(filterErrors);
}

export async function postComplianceRequest(payload) {
  await ensureAuth();
  return fetchWithAuth(`${baseUrl}/compliance-requests`, {
    method: 'POST',
    body: JSON.stringify(payload),
    headers: {
      'Content-Type': 'application/json',
    },
  }).catch(filterErrors);
}

export async function verifyComplianceRequest(complianceRequestId, token) {
  return fetchWithAuth(
    `${baseUrl}/compliance-requests/${complianceRequestId}/verify/${token}`,
    {
      method: 'POST',
    },
  ).catch(filterErrors);
}

export async function getConsentTypes() {
  await ensureAuth();
  return fetchWithAuth(`${baseUrl}/consent-types`).catch(filterErrors);
}

export async function getConsentState() {
  await ensureAuth();

  return fetchWithAuth(`${baseUrl}/consent-state`).catch(filterErrors);
}

export async function postConsentEvents(payload) {
  await ensureAuth();
  return fetchWithAuth(`${baseUrl}/consent-events`, {
    method: 'POST',
    body: JSON.stringify(payload),
    headers: {
      'Content-Type': 'application/json',
    },
  }).catch(filterErrors);
}

export async function getComplianceRequests() {
  await ensureAuth();
  return fetchWithAuth(`${baseUrl}/compliance-requests`).catch(filterErrors);
}

export async function sendComplianceRequestDetail(
  complianceRequestId,
  payload,
) {
  await ensureAuth();
  return fetchWithAuth(
    `${baseUrl}/compliance-requests/${complianceRequestId}/send-details`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
      headers: {
        'Content-Type': 'application/json',
      },
    },
  ).catch(filterErrors);
}

export async function postTrackers(payload) {
  await ensureAuth();
  return fetchWithAuth(`${baseUrl}/trackers`, {
    method: 'POST',
    body: JSON.stringify(payload),
    headers: {
      'Content-Type': 'application/json',
    },
  }).catch(filterErrors);
}
